import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../Styles/OrderFulfillment.css';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { getDashboardSummary, getPendingOrders } from '../utils/orderFulfillmentApi';
import OrderFulfillmentChatWidget from './OrderFulfillmentChatWidget';

const DEFAULT_FILTERS = {
  risk_level: 'all',
  plant_key: '',
  customer_name: '',
  days_left_max: '',
  limit: 120,
};

const ROOT_CAUSE_COLORS = ['#e5242a', '#da7a00', '#f97316', '#1f67be', '#6b7280'];

const TABS = [
  { id: 'risk', label: 'Risk Dashboard' },
  { id: 'unshipped', label: 'Unshipped Orders' },
  { id: 'factory', label: 'Factory Dashboard' },
];

function formatValue(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  return value ?? '-';
}

function parseRootCause(order) {
  const text = `${order?.primary_risk_driver || ''} ${order?.risk_reasons?.join(' ') || ''}`.toLowerCase();
  if (text.includes('spec')) return 'Spec delays';
  if (text.includes('po') || text.includes('pgi') || text.includes('approval')) return 'PR/PO lag';
  if (text.includes('material') || text.includes('stock') || text.includes('shortage')) return 'Material shortage';
  if (text.includes('plant') || text.includes('capacity')) return 'Plant capacity';
  return 'Sampling delay';
}

function buildSpecStatus(order) {
  const t = `${order?.primary_risk_driver || ''}`.toLowerCase();
  return t.includes('spec') ? { label: 'NOT FROZEN', type: 'danger' } : { label: 'Frozen', type: 'ok' };
}

function buildPoStatus(order) {
  const missingPgi = Number(order?.missing_pgi_count || 0) > 0;
  return missingPgi ? { label: 'PO DELAYED', type: 'warn' } : { label: 'PO placed', type: 'ok' };
}

function buildMaterialStatus(order) {
  const lowStock = Number(order?.low_stock_line_count || 0) > 0;
  if (lowStock) return { label: 'SHORTAGE', type: 'danger' };
  if (Number(order?.blocked_stock || 0) > 0) return { label: 'Partial', type: 'warn' };
  return { label: 'Ready', type: 'ok' };
}

const OrderFulfillment = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('risk');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');

  const [pendingData, setPendingData] = useState({ count: 0, items: [] });
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState('');

  const [unshippedRisk, setUnshippedRisk] = useState('all');
  const [unshippedFactory, setUnshippedFactory] = useState('all');
  const [unshippedBuyer, setUnshippedBuyer] = useState('all');
  const [shipDaysMax, setShipDaysMax] = useState(30);
  const [selectedFactory, setSelectedFactory] = useState('all');

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError('');
      const data = await getDashboardSummary();
      setSummary(data);
    } catch (error) {
      setSummaryError(error.message || 'Failed to load dashboard summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadPendingOrders = useCallback(async (activeFilters) => {
    try {
      setPendingLoading(true);
      setPendingError('');
      const payload = {
        ...activeFilters,
        limit: Math.min(Number(activeFilters.limit) || 120, 250),
      };
      const data = await getPendingOrders(payload);
      setPendingData({
        count: data?.count || 0,
        items: Array.isArray(data?.items) ? data.items : [],
      });
    } catch (error) {
      setPendingError(error.message || 'Failed to load pending orders');
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    loadPendingOrders(DEFAULT_FILTERS);
  }, [loadSummary, loadPendingOrders]);

  const generatedAtText = useMemo(() => {
    if (!summary?.generated_at) return '-';
    return new Date(summary.generated_at).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }, [summary]);

  const allOrders = pendingData.items || [];

  const kpiData = useMemo(() => {
    const totalUnshipped = summary?.kpis?.pending_orders || pendingData.count || 0;
    const highRisk = summary?.kpis?.high_risk_orders || allOrders.filter((o) => o.risk_level === 'high').length;
    const shippingThisWeek = summary?.kpis?.shipping_this_week || 0;
    const onTrack = Math.max(totalUnshipped - highRisk, 0);
    return { totalUnshipped, highRisk, shippingThisWeek, onTrack };
  }, [summary, pendingData.count, allOrders]);

  const topAttentionOrders = useMemo(() => {
    return [...allOrders]
      .sort((a, b) => Number(b.risk_score || 0) - Number(a.risk_score || 0))
      .slice(0, 6);
  }, [allOrders]);

  const riskByFactoryData = useMemo(() => {
    const plantMap = new Map();
    allOrders.forEach((order) => {
      const key = `Plant ${order.plant_key || '-'}`;
      const previous = plantMap.get(key) || 0;
      plantMap.set(key, previous + 1);
    });
    return [...plantMap.entries()]
      .map(([name, orders]) => ({ name, orders }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 4);
  }, [allOrders]);

  const rootCauseData = useMemo(() => {
    const causeMap = new Map();
    topAttentionOrders.forEach((order) => {
      const cause = parseRootCause(order);
      causeMap.set(cause, (causeMap.get(cause) || 0) + 1);
    });
    const total = topAttentionOrders.length || 1;

    return [...causeMap.entries()]
      .map(([name, value], idx) => ({
        name,
        value,
        percent: Math.round((value / total) * 100),
        color: ROOT_CAUSE_COLORS[idx % ROOT_CAUSE_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [topAttentionOrders]);

  const factoryOptions = useMemo(() => {
    return ['all', ...new Set(allOrders.map((order) => String(order.plant_key || '')))].filter(Boolean);
  }, [allOrders]);

  const buyerOptions = useMemo(() => {
    return ['all', ...new Set(allOrders.map((order) => order.customer_name || ''))].filter(Boolean);
  }, [allOrders]);

  const unshippedRows = useMemo(() => {
    return allOrders.filter((order) => {
      if (unshippedRisk !== 'all' && String(order.risk_level || 'low').toLowerCase() !== unshippedRisk) return false;
      if (unshippedFactory !== 'all' && String(order.plant_key || '') !== unshippedFactory) return false;
      if (unshippedBuyer !== 'all' && String(order.customer_name || '') !== unshippedBuyer) return false;
      if (Number.isFinite(Number(shipDaysMax)) && Number(order.days_left || 0) > Number(shipDaysMax)) return false;
      return true;
    });
  }, [allOrders, unshippedRisk, unshippedFactory, unshippedBuyer, shipDaysMax]);

  const activeFactory = useMemo(() => {
    if (selectedFactory !== 'all') return selectedFactory;
    if (factoryOptions.length > 1) return factoryOptions[1];
    return 'all';
  }, [selectedFactory, factoryOptions]);

  const factoryOrders = useMemo(() => {
    if (activeFactory === 'all') return allOrders;
    return allOrders.filter((order) => String(order.plant_key || '') === activeFactory);
  }, [allOrders, activeFactory]);

  const capacityUtilized = useMemo(() => {
    const maxLoadReference = Math.max(...factoryOptions.slice(1).map((plant) => allOrders.filter((o) => String(o.plant_key || '') === plant).length), 12);
    const current = factoryOrders.length;
    return Math.min(100, Math.round((current / maxLoadReference) * 100));
  }, [factoryOrders.length, factoryOptions, allOrders]);

  const factoryKpis = useMemo(() => {
    const highRisk = factoryOrders.filter((order) => order.risk_level === 'high').length;
    const materialsReady = factoryOrders.filter((order) => Number(order.low_stock_line_count || 0) === 0).length;
    const shippingThisWeek = factoryOrders.filter((order) => Number(order.days_left || 999) >= 0 && Number(order.days_left || 999) <= 7).length;
    return {
      ordersInPlant: factoryOrders.length,
      highRisk,
      materialsReady,
      shippingThisWeek,
    };
  }, [factoryOrders]);

  const weeklyBreakdown = useMemo(() => {
    const weekBuckets = [0, 0, 0, 0];
    factoryOrders.forEach((order) => {
      const date = new Date(order.requested_delivery_date || order.sale_order_date || Date.now());
      const day = date.getDate();
      const bucket = Math.min(3, Math.floor((day - 1) / 7));
      weekBuckets[bucket] += 1;
    });
    return weekBuckets.map((count, index) => ({
      label: `Week ${index + 1}`,
      count,
    }));
  }, [factoryOrders]);

  const readinessStats = useMemo(() => {
    const total = factoryOrders.length || 1;
    const ready = factoryOrders.filter((o) => Number(o.low_stock_line_count || 0) === 0).length;
    const partial = factoryOrders.filter((o) => Number(o.low_stock_line_count || 0) > 0 && Number(o.blocked_stock || 0) === 0).length;
    const notReady = Math.max(factoryOrders.length - ready - partial, 0);
    return {
      ready: Math.round((ready / total) * 100),
      partial: Math.round((partial / total) * 100),
      notReady: Math.round((notReady / total) * 100),
    };
  }, [factoryOrders]);

  const riskRowsByFactory = useMemo(() => {
    return factoryOrders
      .filter((o) => o.risk_level === 'high' || Number(o.risk_score || 0) >= 70)
      .sort((a, b) => Number(b.risk_score || 0) - Number(a.risk_score || 0))
      .slice(0, 8);
  }, [factoryOrders]);

  const gaugeAngle = useMemo(() => {
    return Math.max(-90, Math.min(90, (capacityUtilized / 100) * 180 - 90));
  }, [capacityUtilized]);

  return (
    <div className="of-app-shell">
      <header className="of-topbar">
        <h1>Risk Dashboard - Management</h1>
        <div className="of-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button type="button" className="of-tab-btn" onClick={() => navigate('/home')}>
            Back to Home
          </button>
          <span>{generatedAtText}</span>
        </div>
      </header>

      <nav className="of-tabs" role="tablist" aria-label="Order fulfillment tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`of-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {(summaryLoading || pendingLoading) && <p className="of-loading">Loading latest data...</p>}
      {(summaryError || pendingError) && <p className="of-error">{summaryError || pendingError}</p>}

      {activeTab === 'risk' && (
        <section className="of-tab-panel of-enter">
          <div className="of-cards-row">
            <div className="of-kpi-card clean blue of-float delay-1">
              <p>TOTAL UNSHIPPED</p>
              <strong>{formatValue(kpiData.totalUnshipped)}</strong>
            </div>
            <div className="of-kpi-card clean red of-float delay-2">
              <p>HIGH RISK</p>
              <strong>{formatValue(kpiData.highRisk)}</strong>
              <span>Need action today</span>
            </div>
            <div className="of-kpi-card clean orange of-float delay-3">
              <p>SHIPPING THIS WEEK</p>
              <strong>{formatValue(kpiData.shippingThisWeek)}</strong>
              <span>Low buffer</span>
            </div>
            <div className="of-kpi-card clean green of-float delay-4">
              <p>ON TRACK</p>
              <strong>{formatValue(kpiData.onTrack)}</strong>
              <span>Healthy pipeline</span>
            </div>
          </div>

          <div className="of-card of-elevate delay-1">
            <h2>Orders Needing Attention Today</h2>
            <p className="of-subtext">AI-ranked by risk score. These {topAttentionOrders.length} orders need action to meet delivery commitments.</p>

            <div className="of-table-wrap of-scroll-glow">
              <table className="of-table attention">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Buyer</th>
                    <th>Factory Ex-Factory</th>
                    <th>Days Left</th>
                    <th>Risk Score</th>
                    <th>Primary Risk Driver</th>
                    <th>Who Should Act</th>
                    <th>AI Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {topAttentionOrders.map((order) => (
                    <tr key={order.order_id}>
                      <td className="linkish">{order.order_id}</td>
                      <td>{order.customer_name}</td>
                      <td>
                        <div>Plant {order.plant_key}</div>
                        <div>{order.requested_delivery_date}</div>
                      </td>
                      <td className="days danger">{Math.abs(Number(order.days_left || 0))} days</td>
                      <td><span className="risk-bubble">{formatValue(order.risk_score)}</span></td>
                      <td>{order.primary_risk_driver || '-'}</td>
                      <td><span className="tag blue">Merchandising</span></td>
                      <td><span className="tag signal">{order.risk_reasons?.[0] || order.primary_risk_driver || '-'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="of-chart-row">
            <div className="of-card of-elevate delay-2">
              <h3>Risk by Factory</h3>
              <div className="of-chart-box">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={riskByFactoryData} layout="vertical" margin={{ left: 20, right: 10, top: 10, bottom: 10 }}>
                    <CartesianGrid stroke="#eceff5" horizontal={false} />
                    <XAxis type="number" stroke="#8a98ab" />
                    <YAxis type="category" dataKey="name" stroke="#5d6b7d" width={90} />
                    <Tooltip />
                    <Bar dataKey="orders" radius={[0, 8, 8, 0]}>
                      {riskByFactoryData.map((entry, index) => (
                        <Cell key={entry.name} fill={index < 2 ? '#e5242a' : index === 2 ? '#da7a00' : '#16a34a'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {riskByFactoryData[0] && (
                <p className="of-subtext">
                  {riskByFactoryData[0].name} is overloaded - {riskByFactoryData[0].orders} orders competing for capacity
                </p>
              )}
            </div>

            <div className="of-card of-elevate delay-3">
              <h3>Risk Breakdown by Root Cause</h3>
              <div className="of-donut-layout">
                <div className="of-donut-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={rootCauseData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={58}
                        outerRadius={92}
                        paddingAngle={2}
                      >
                        {rootCauseData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="of-legend-list">
                  {rootCauseData.map((item) => (
                    <li key={item.name}>
                      <span className="dot" style={{ backgroundColor: item.color }} />
                      <span>{item.name}</span>
                      <strong>{item.percent}%</strong>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="of-subtext">{topAttentionOrders.length} orders</p>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'unshipped' && (
        <section className="of-tab-panel of-enter">
          <div className="of-card of-elevate">
            <div className="of-filter-row">
              <div className="risk-group">
                <span>Risk:</span>
                {['all', 'high', 'medium', 'low'].map((level) => (
                  <button
                    key={level}
                    type="button"
                    className={`chip-btn ${unshippedRisk === level ? 'active' : ''}`}
                    onClick={() => setUnshippedRisk(level)}
                  >
                    {level[0].toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>

              <select value={unshippedFactory} onChange={(e) => setUnshippedFactory(e.target.value)}>
                {factoryOptions.map((factory) => (
                  <option key={factory} value={factory}>{factory === 'all' ? 'Factory' : `Plant ${factory}`}</option>
                ))}
              </select>

              <select value={unshippedBuyer} onChange={(e) => setUnshippedBuyer(e.target.value)}>
                {buyerOptions.map((buyer) => (
                  <option key={buyer} value={buyer}>{buyer === 'all' ? 'Buyer' : buyer}</option>
                ))}
              </select>

              <button type="button" className="chip-btn">Days to Ship: {'<'} {shipDaysMax} days</button>

              <div className="orders-summary">{unshippedRows.length} orders - <span>{unshippedRows.filter((o) => o.risk_level === 'high').length} at risk</span></div>
            </div>

            <div className="of-table-wrap of-scroll-glow">
              <table className="of-table unshipped">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Buyer</th>
                    <th>Style</th>
                    <th>Factory</th>
                    <th>Ex-Factory Date</th>
                    <th>Days Left</th>
                    {/* <th>Ageing</th> */}
                    {/* <th>Spec Status</th> */}
                    <th>PR/PO Status</th>
                    <th>Material</th>
                    <th>Risk Score</th>
                    {/* <th>Action</th> */}
                  </tr>
                </thead>
                <tbody>
                  {unshippedRows.map((order) => {
                    const spec = buildSpecStatus(order);
                    const po = buildPoStatus(order);
                    const material = buildMaterialStatus(order);
                    const overdue = Number(order.days_left || 0) < 0;

                    return (
                      <tr key={order.order_id}>
                        <td className="linkish">{order.order_id}</td>
                        <td>{order.customer_name}</td>
                        <td>{order.sample_material || '-'}</td>
                        <td>Plant {order.plant_key}</td>
                        <td>{order.requested_delivery_date}</td>
                        <td className="days danger">{Math.abs(Number(order.days_left || 0))}</td>
                        {/* <td>{overdue ? `+${Math.abs(Number(order.days_left || 0))} days overdue` : 'On schedule'}</td> */}
                        {/* <td><span className={`badge ${spec.type}`}>{spec.label}</span></td> */}
                        <td><span className={`badge ${po.type}`}>{po.label}</span></td>
                        <td><span className={`badge ${material.type}`}>{material.label}</span></td>
                        <td><span className="risk-bubble">{formatValue(order.risk_score)}</span></td>
                        {/* <td className="action-link">Review now ?</td> */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'factory' && (
        <section className="of-tab-panel of-enter">
          <div className="of-card of-elevate">
            <div className="of-filter-row">
              <div className="risk-group">
                <span>Factory:</span>
                <select value={activeFactory} onChange={(e) => setSelectedFactory(e.target.value)}>
                  {factoryOptions.map((factory) => (
                    <option key={factory} value={factory}>{factory === 'all' ? 'All Plants' : `Plant ${factory}`}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="of-cards-row four">
              <div className="of-mini-kpi of-float delay-1"><p>ORDERS IN PLANT</p><strong>{factoryKpis.ordersInPlant}</strong></div>
              <div className="of-mini-kpi of-float delay-2"><p>HIGH RISK</p><strong className="red">{factoryKpis.highRisk}</strong><span>Capacity pressure</span></div>
              <div className="of-mini-kpi of-float delay-3"><p>MATERIALS READY</p><strong className="green">{factoryKpis.materialsReady}</strong><span>{readinessStats.ready}%</span></div>
              <div className="of-mini-kpi of-float delay-4"><p>SHIPPING THIS WEEK</p><strong className="orange">{factoryKpis.shippingThisWeek}</strong></div>
            </div>

            <div className="of-factory-grid">
              <div className="factory-left">
                <h3>{activeFactory === 'all' ? 'Factory Capacity Load' : `Plant ${activeFactory} Capacity Load`}</h3>
                <div className="gauge-wrap of-elevate">
                  <div className="gauge-arc">
                    <div className="gauge-mask" />
                    <div className="gauge-needle" style={{ transform: `rotate(${gaugeAngle}deg)` }} />
                    <div className="gauge-center" />
                  </div>
                  <p className="gauge-value">{capacityUtilized}%</p>
                  <p className="of-subtext">Capacity Utilized</p>
                </div>

                <div className="capacity-alert">Alert: Overloaded - {factoryKpis.highRisk} orders competing for floor space</div>

                <div className="weekly-breakdown">
                  <h4>Weekly Breakdown</h4>
                  {weeklyBreakdown.map((week) => (
                    <div key={week.label} className="week-row">
                      <span>{week.label}</span>
                      <div className="week-bar"><i style={{ width: `${Math.min(100, (week.count / Math.max(1, factoryKpis.ordersInPlant)) * 100)}%` }} /></div>
                      <strong>{week.count} orders</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div className="factory-right">
                <h3>My Orders - At Risk</h3>
                <p className="of-subtext">Filtered to Plant {activeFactory} high risk only</p>
                <div className="of-table-wrap of-scroll-glow">
                  <table className="of-table compact">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Buyer</th>
                        <th>Style</th>
                        <th>Ex-Factory</th>
                        <th>Days Left</th>
                        <th>Material Status</th>
                        <th>Blocker</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {riskRowsByFactory.map((order) => (
                        <tr key={order.order_id}>
                          <td className="linkish">{order.order_id}</td>
                          <td>{order.customer_name}</td>
                          <td>{order.sample_material || '-'}</td>
                          <td>{order.requested_delivery_date}</td>
                          <td className="days danger">{Math.abs(Number(order.days_left || 0))}</td>
                          <td>{buildMaterialStatus(order).label}</td>
                          <td>{order.primary_risk_driver || '-'}</td>
                          <td className="action-link">Review</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="material-readiness">
              <h4>Material Readiness by Order</h4>
              <div className="stack-bar">
                <span className="ready" style={{ width: `${readinessStats.ready}%` }}>{readinessStats.ready}%</span>
                <span className="partial" style={{ width: `${readinessStats.partial}%` }}>{readinessStats.partial}%</span>
                <span className="not-ready" style={{ width: `${readinessStats.notReady}%` }}>{readinessStats.notReady}%</span>
              </div>
            </div>
          </div>
        </section>
      )}

      <OrderFulfillmentChatWidget />

    </div>
  );
};

export default OrderFulfillment;
